import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useTRPC } from "@/trpc";
import type {
  RunScope,
  StartedRun,
} from "@/features/credentials/components/primary-source-runs/types";
import {
  StartedRunNotice,
  type ProviderPageSearch,
} from "./provider-page/provider-page-search";

const PROVIDER_PAGE_ROUTE =
  "/org/bu/$organizationBusinessUnitId/providers/credentials/$affiliationId";

export function useOpenProviderPage(organizationBusinessUnitId: string) {
  const navigate = useNavigate();
  return useCallback(
    ({
      affiliationId,
      search = {},
    }: {
      affiliationId: string;
      search?: ProviderPageSearch;
    }) =>
      navigate({
        to: PROVIDER_PAGE_ROUTE,
        params: { organizationBusinessUnitId, affiliationId },
        search,
      }),
    [navigate, organizationBusinessUnitId],
  );
}

type StartScope = Pick<
  RunScope,
  "organizationBusinessUnitId" | "providerUserId" | "expectedAffiliationId"
>;

/** What both the roster and the provider page do once a run has started:
 * refresh what shows runs, then show the provider's report for it. */
export function useShowStartedRun({
  scope,
  affiliationId,
}: {
  scope: StartScope;
  affiliationId: string;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const openProviderPage = useOpenProviderPage(
    scope.organizationBusinessUnitId,
  );
  const { organizationBusinessUnitId } = scope;
  return useCallback(
    async (request: StartedRun) => {
      await queryClient.invalidateQueries({
        queryKey: trpc.psv.listRuns.infiniteQueryKey(scope),
      });
      void queryClient.invalidateQueries({
        queryKey: trpc.psv.getProviderScreenings.queryKey(scope),
      });
      void queryClient.invalidateQueries({
        queryKey: trpc.providerWorkspace.list.queryKey({
          organizationBusinessUnitId,
        }),
      });
      let notice: StartedRunNotice | undefined;
      if (request.reused) {
        notice = StartedRunNotice.Reused;
      } else if (request.dispatchPending) {
        notice = StartedRunNotice.DispatchPending;
      }
      await openProviderPage({
        affiliationId,
        search: { notice },
      });
    },
    [
      affiliationId,
      openProviderPage,
      organizationBusinessUnitId,
      queryClient,
      scope,
      trpc,
    ],
  );
}
